import React, { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@material-ui/core';
import { SidebarButtonGroup } from '../../../shared/components';
import { DifferentiationScreenAction } from './DifferentiationScreenAction';
import { ScreenProps } from './DifferentiationsContainer';
import { preferredDifferentiationMapper } from '../../../../config/api/models/Differentiation';
import { useI18nPath } from '../../../../config/clientRoutes/hooks';
import { DifferentiationsListWrapper, DifferentiationsOverviewWrapper } from './Atoms';
import { useFetch } from '../../../shared';
import apiRoutes from '../../../../config/api/routes';
import { useFullBook } from '../../../shared/hooks';
import { useHistory } from 'react-router';
import { ITrajectoryDifferentiation } from 'src/config/api/models/Trajectory';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';
import NewDifferentiationListItem from './NewDifferentiationListItem/DifferentiationListItem';
import { useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { useSnackbar } from 'notistack';

type Props = ScreenProps & {
  currentVersionId: string;
  differentiations: ITrajectoryDifferentiation[];
  onUpdate: () => void;
  preferredDifferentiation: string;
};

export const DifferentiationsOverview: FC<Props> = ({
  openCallback,
  bookId,
  chapterId,
  trajectoryId,
  currentVersionId,
  differentiations,
  onUpdate,
  preferredDifferentiation,
}) => {
  const trajectory = useAppSelector((state) => state.trajectories.trajectory);
  const currentTrajectoryId = useAppSelector((state) => state.trajectories.currentTrajectoryId);
  const { t } = useTranslation();
  const { refetch: refetchFullBook } = useFullBook(bookId);
  const [
    postPreferredDifferentiationResponse,
    postPreferredDifferentiation,
    resetPostPreferredDifferentiation,
  ] = useFetch(apiRoutes.preferredDifferentiation(trajectoryId), {
    method: 'POST',
    mapper: preferredDifferentiationMapper,
  });
  const history = useHistory();
  const { getPath } = useI18nPath();

  const handleSetPreferredDifferentiation = useCallback(
    (differentiationId: string) => {
      if (differentiationId !== preferredDifferentiation) {
        postPreferredDifferentiation({
          body: {
            differentiation_id: differentiationId,
          },
        });
      }
    },
    [preferredDifferentiation, postPreferredDifferentiation]
  );

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (postPreferredDifferentiationResponse.success) {
      const newPreferedId = postPreferredDifferentiationResponse.data?.preferredId;

      refetchFullBook();

      if (newPreferedId) {
        if (newPreferedId !== currentVersionId) {
          history.push(
            getPath('trajectoryView', {
              bookId,
              chapterId,
              trajectoryId: newPreferedId,
            })
          );
        } else {
          resetPostPreferredDifferentiation();
          onUpdate();
        }
      } else if (trajectoryId !== currentVersionId) {
        history.push(
          getPath('trajectoryView', {
            bookId,
            chapterId,
            trajectoryId,
          })
        );
      } else {
        resetPostPreferredDifferentiation();
        onUpdate();
      }

      const diffTitle = differentiations.find(
        (diff) => diff.id === postPreferredDifferentiationResponse.data?.preferredId
      )?.title;
      const titleToSnakBar = diffTitle ? diffTitle : t('differentiations.default');
      enqueueSnackbar(`${titleToSnakBar} is nu de versie die standaard geopend wordt`, {
        autoHideDuration: 2000,
        variant: 'success',
        style: { zIndex: '10000 !important' },
      });
    }
  }, [
    bookId,
    chapterId,
    currentVersionId,
    differentiations,
    enqueueSnackbar,
    getPath,
    history,
    onUpdate,
    postPreferredDifferentiationResponse,
    refetchFullBook,
    resetPostPreferredDifferentiation,
    t,
    trajectoryId,
  ]);

  const handleWrapperClick = (trajectoryId: ITrajectoryDifferentiation['id']) => {
    if (currentTrajectoryId === trajectoryId) return;
    refetchTrajectory(trajectoryId);
  };

  const refetchTrajectory = React.useCallback(
    (trajectoryId: ITrajectoryDifferentiation['id']) => {
      history.push(
        getPath('trajectoryView', {
          bookId,
          chapterId,
          trajectoryId,
        })
      );
    },
    [bookId, chapterId, history, getPath]
  );

  return differentiations ? (
    <DifferentiationsOverviewWrapper>
      <Typography style={{ marginBottom: '1rem' }}>{t('differentiations.description')}</Typography>
      <DifferentiationsListWrapper onClick={() => handleWrapperClick(trajectoryId)}>
        <NewDifferentiationListItem
          preferredVersion={preferredDifferentiation}
          onChangePreferredVersion={() => handleSetPreferredDifferentiation(trajectoryId)}
          withPreferenceSelection={differentiations.length > 0}
          version={{
            id: trajectoryId,
            ownerName: 'Lernova',
            title: t('differentiations.default'),
            isCollab: false,
            parentId: trajectoryId,
            isPreferred: false,
            isOwner: false,
            isDefaultVersion: true,
            createdAt: (trajectory?.parentCreatedAt ?? trajectory?.createdAt) as Date,
            amountOfVisibleGroups: 0,
          }}
          onUpdate={onUpdate}
          trajectoryId={trajectoryId}
          openCallback={openCallback}
          refetchTrajectory={refetchTrajectory}
          isCurrentVersion={currentVersionId === trajectoryId}
        />
      </DifferentiationsListWrapper>
      <SidebarButtonGroup
        icon={SpriteIconsLight.CIRCLE_PLUS}
        submitText={t('differentiations.addVersionButton')}
        submitCallback={() => openCallback(DifferentiationScreenAction.CREATE)}
      />
      <DifferentiationsListWrapper>
        {differentiations.length > 0 && <Typography variant="h2">Jouw versies</Typography>}
        {differentiations.map((version: ITrajectoryDifferentiation) => (
          <NewDifferentiationListItem
            preferredVersion={preferredDifferentiation}
            key={version.id}
            onChangePreferredVersion={() => handleSetPreferredDifferentiation(version.id)}
            withPreferenceSelection={differentiations.length > 0}
            version={version}
            onUpdate={onUpdate}
            trajectoryId={trajectoryId}
            openCallback={openCallback}
            refetchTrajectory={refetchTrajectory}
            isCurrentVersion={version?.id === currentTrajectoryId}
          />
        ))}
      </DifferentiationsListWrapper>
    </DifferentiationsOverviewWrapper>
  ) : null;
};

/* TODO - NEEDS TRANSLATION t('differentiations.default') should be changed to Standaardversie*/
